import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { axiosClient } from '../../utils/auth.util.js';

const saveTokenToLocalStorage = (token) => {
  if (token) {
    localStorage.setItem('authToken', token);
  }
};

const removeTokenFromLocalStorage = () => {
  localStorage.removeItem('authToken');
};

const getTokenFromLocalStorage = () => {
  return localStorage.getItem('authToken');
};

const initialState = {
  userData: null,
  isOtpSent: false,
  submittedPhoneNumber: '',
  isLoggedIn: !!getTokenFromLocalStorage(),
  authToken: getTokenFromLocalStorage(),
  isLoading: false,
  isOpenBasicInfoModal: false,
  error: null,
  isOpenLogin: false,
  
  // Dashboard-specific state
  engagements: [],
  totalEngagements: 0,
  totalPages: 1,
  currentPage: 1,
  engagementsLoading: false,
  engagementsError: null,
  
  // Payout-specific state
  payoutData: [],
  payoutLoading: false,
  payoutError: null,
  
  // Wallet transactions state
  walletTransactions: [],
  walletPagination: {
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    hasNextPage: false,
    hasPrevPage: false
  },
  walletTransactionsLoading: false,
  walletTransactionsError: null,
  
  // Wallet loading state for transactions
  walletLoading: false,
  
  // Profile update state
  profileUpdateLoading: false,
  profileUpdateError: null,
};

// Existing async thunks
export const sendOtpAsync = createAsyncThunk(
  'auth/sendOtp',
  async (data, { rejectWithValue }) => {
    try {
      const response = await axiosClient.post(
        `/users/send-otp/expert?login_type=${data.loginType}`,
        data.payload
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: 'Failed to send OTP' });
    }
  }
);

export const loginAsync = createAsyncThunk(
  'auth/loginAsync',
  async (data, { rejectWithValue }) => {
    try {
      const response = await axiosClient.post(`/users/login/expert`, data.payload);
      if (response?.data?.token) {
        saveTokenToLocalStorage(response.data.token);
      }
      return { data: response.data.result, token: response?.data?.token };
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: 'Login failed' });
    }
  }
);

export const verifyUser = createAsyncThunk(
  'auth/verifyUser',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosClient.post(`/users/verify-user/expert`);
      return response.data.result;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: 'User verification failed' });
    }
  }
);

// New async thunks for dashboard functionality
export const fetchEngagementsAsync = createAsyncThunk(
  'auth/fetchEngagements',
  async ({ expertId, statusFilter, taskStatus, page = 1, limit = 12 }, { rejectWithValue }) => {
    try {
      let params = {
        status: statusFilter,
        page: page,
        limit: limit
      };
      if (taskStatus) {
        params.taskStatus = taskStatus;
      }
      
      const response = await axiosClient.get(
        `expert/engagements/${expertId}`,
        { params }
      );
      
      return {
        engagements: response.data.result,
        totalPages: response.data.totalPages,
        totalEngagements: response.data.totalOrders,
        currentPage: response.data.currentPage,
      };
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: 'Failed to fetch engagements' });
    }
  }
);

export const fetchPayoutDataAsync = createAsyncThunk(
  'auth/fetchPayoutData',
  async (expertId, { rejectWithValue }) => {
    try {
      const params = {
        status: 'notified',
        page: 1,
        limit: 12
      };
      
      const response = await axiosClient.get(`expert/payout/${expertId}`, { params });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: 'Failed to fetch payout data' });
    }
  }
);

export const fetchWalletTransactionsAsync = createAsyncThunk(
  'auth/fetchWalletTransactions',
  async ({ expertId, page = 1, limit = 10, type, status }, { rejectWithValue }) => {
    try {
      const params = {
        page,
        limit
      };
      
      if (type) params.type = type;
      if (status) params.status = status;
      
      const response = await axiosClient.get(`expert/wallet-payouts/${expertId}`, { params });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: 'Failed to fetch wallet transactions' });
    }
  }
);

export const updateProfilePictureAsync = createAsyncThunk(
  'auth/updateProfilePicture',
  async ({ expertId, formData }, { rejectWithValue }) => {
    try {
      const response = await axiosClient.put(`/expert/profile-picture/${expertId}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: 'Failed to update profile picture' });
    }
  }
);

export const expressInterestAsync = createAsyncThunk(
  'auth/expressInterest',
  async (engagementId, { rejectWithValue }) => {
    try {
      const response = await axiosClient.put(`expert/update-engagement/${engagementId}/expert`, {
        status: 'interested',
        expressedInterestAt: new Date().toISOString()
      });
      return { engagementId, data: response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: 'Failed to express interest' });
    }
  }
);

export const startWorkAsync = createAsyncThunk(
  'auth/startWork',
  async (engagementId, { rejectWithValue }) => {
    try {
      const response = await axiosClient.put(`expert/update-engagement/${engagementId}/expert`, {
        taskStatus: 'in-progress'
      });
      return { engagementId, data: response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: 'Failed to start work' });
    }
  }
);

export const completeWorkAsync = createAsyncThunk(
  'auth/completeWork',
  async (engagementId, { rejectWithValue }) => {
    try {
      const response = await axiosClient.put(`expert/update-engagement/${engagementId}/expert`, {
        taskStatus: 'completed'
      });
      return { engagementId, data: response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: 'Failed to complete work' });
    }
  }
);

export const authSlice = createSlice({
  name: 'auth',
  initialState,

  reducers: {
    logout: (state) => {
      Object.assign(state, initialState, {
        isLoggedIn: false,
        authToken: null,
      });
      removeTokenFromLocalStorage();
    },
    
    setIsLoading: (state, action) => {
      state.isLoading = action.payload;
    },
    
    setIsOpenLogin: (state, action) => {
      state.isOpenLogin = action.payload;
    },
    
    setAuthToken: (state, action) => {
      state.authToken = action.payload;
      if (action.payload) {
        saveTokenToLocalStorage(action.payload);
        state.isLoggedIn = true;
      }
    },
    
    setSubmittedEmail: (state, action) => {
      state.submittedPhoneNumber = action.payload;
    },
    
    setIsOpenBasicModal: (state, action) => {
      state.isOpenBasicInfoModal = action.payload;
    },
    
    setResetLoginInfo: (state) => {
      state.isOtpSent = false;
      state.isLoggedIn = false;
      state.isLoading = false;
      state.error = null;
    },
    
    clearError: (state) => {
      state.error = null;
    },

    clearEngagementsError: (state) => {
      state.engagementsError = null;
    },

    clearPayoutError: (state) => {
      state.payoutError = null;
    },

    clearProfileUpdateError: (state) => {
      state.profileUpdateError = null;
    },

    clearWalletTransactionsError: (state) => {
      state.walletTransactionsError = null;
    },

    updateEngagementStatus: (state, action) => {
      const { engagementId, status, taskStatus } = action.payload;
      const engagement = state.engagements.find(eng => eng._id === engagementId);
      if (engagement) {
        if (status) engagement.status = status;
        if (taskStatus) engagement.taskStatus = taskStatus;
      }
    },

    setCurrentPage: (state, action) => {
      state.currentPage = action.payload;
    },
  },

  extraReducers: (builder) => {
    // Send OTP
    builder
      .addCase(sendOtpAsync.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(sendOtpAsync.fulfilled, (state) => {
        state.isLoading = false;
        state.isOtpSent = true;
        state.error = null;
      })
      .addCase(sendOtpAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload?.message || 'Failed to send OTP';
      });

    // Login
    builder
      .addCase(loginAsync.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(loginAsync.fulfilled, (state, action) => {
        const { data, token } = action.payload;
        state.isLoading = false;
        state.isLoggedIn = true;
        state.error = null;
        state.isOpenLogin = false;
        state.authToken = token;
        state.userData = data;
      })
      .addCase(loginAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload?.message || 'Login failed';
      });

    // Verify user
    builder
      .addCase(verifyUser.pending, (state) => {
        state.isLoading = true;
        state.walletLoading = true;
        state.error = null;
      })
      .addCase(verifyUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.walletLoading = false;
        state.isLoggedIn = true;
        state.error = null;
        state.userData = action.payload;
      })
      .addCase(verifyUser.rejected, (state, action) => {
        state.isLoading = false;
        state.walletLoading = false;
        state.error = action.payload?.message || 'User verification failed';
        state.userData = null;
        state.authToken = null;
        state.isLoggedIn = false;
        removeTokenFromLocalStorage();
      });

    // Fetch Engagements
    builder
      .addCase(fetchEngagementsAsync.pending, (state) => {
        state.engagementsLoading = true;
        state.engagementsError = null;
      })
      .addCase(fetchEngagementsAsync.fulfilled, (state, action) => {
        state.engagementsLoading = false;
        state.engagements = action.payload.engagements;
        state.totalPages = action.payload.totalPages;
        state.totalEngagements = action.payload.totalEngagements;
        state.currentPage = action.payload.currentPage;
        state.engagementsError = null;
      })
      .addCase(fetchEngagementsAsync.rejected, (state, action) => {
        state.engagementsLoading = false;
        state.engagementsError = action.payload?.message || 'Failed to fetch engagements';
      });

    // Fetch Payout Data
    builder
      .addCase(fetchPayoutDataAsync.pending, (state) => {
        state.payoutLoading = true;
        state.payoutError = null;
      })
      .addCase(fetchPayoutDataAsync.fulfilled, (state, action) => {
        state.payoutLoading = false;
        state.payoutData = action.payload;
        state.payoutError = null;
      })
      .addCase(fetchPayoutDataAsync.rejected, (state, action) => {
        state.payoutLoading = false;
        state.payoutError = action.payload?.message || 'Failed to fetch payout data';
      });

    // Fetch Wallet Transactions
    builder
      .addCase(fetchWalletTransactionsAsync.pending, (state) => {
        state.walletTransactionsLoading = true;
        state.walletTransactionsError = null;
      })
      .addCase(fetchWalletTransactionsAsync.fulfilled, (state, action) => {
        state.walletTransactionsLoading = false;
        state.walletTransactions = action.payload.result;
        state.walletPagination = action.payload.pagination;
        state.walletTransactionsError = null;
      })
      .addCase(fetchWalletTransactionsAsync.rejected, (state, action) => {
        state.walletTransactionsLoading = false;
        state.walletTransactionsError = action.payload?.message || 'Failed to fetch wallet transactions';
      });

    // Update Profile Picture
    builder
      .addCase(updateProfilePictureAsync.pending, (state) => {
        state.profileUpdateLoading = true;
        state.profileUpdateError = null;
      })
      .addCase(updateProfilePictureAsync.fulfilled, (state, action) => {
        state.profileUpdateLoading = false;
        if (state.userData && action.payload.profilePicture) {
          state.userData.profilePicture = action.payload.profilePicture;
        }
        state.profileUpdateError = null;
      })
      .addCase(updateProfilePictureAsync.rejected, (state, action) => {
        state.profileUpdateLoading = false;
        state.profileUpdateError = action.payload?.message || 'Failed to update profile picture';
      });

    // Express Interest
    builder
      .addCase(expressInterestAsync.pending, (state) => {
        // Optional: Add loading state for specific engagement
      })
      .addCase(expressInterestAsync.fulfilled, (state, action) => {
        const { engagementId, data } = action.payload;
        const engagement = state.engagements.find(eng => eng._id === engagementId);
        if (engagement && data.success) {
          engagement.status = 'interested';
          engagement.expressedInterestAt = data.result.expressedInterestAt;
        }
      })
      .addCase(expressInterestAsync.rejected, (state, action) => {
        // Handle error - could show toast or set error state
        state.error = action.payload?.message || 'Failed to express interest';
      });

    // Start Work
    builder
      .addCase(startWorkAsync.pending, (state) => {
        // Optional: Add loading state for specific engagement
      })
      .addCase(startWorkAsync.fulfilled, (state, action) => {
        const { engagementId, data } = action.payload;
        const engagement = state.engagements.find(eng => eng._id === engagementId);
        if (engagement && data.success) {
          engagement.taskStatus = 'in-progress';
        }
      })
      .addCase(startWorkAsync.rejected, (state, action) => {
        state.error = action.payload?.message || 'Failed to start work';
      });

    // Complete Work
    builder
      .addCase(completeWorkAsync.pending, (state) => {
        // Optional: Add loading state for specific engagement
      })
      .addCase(completeWorkAsync.fulfilled, (state, action) => {
        const { engagementId, data } = action.payload;
        const engagement = state.engagements.find(eng => eng._id === engagementId);
        if (engagement && data.success) {
          engagement.taskStatus = 'completed';
        }
      })
      .addCase(completeWorkAsync.rejected, (state, action) => {
        state.error = action.payload?.message || 'Failed to complete work';
      });
  },
});

export const {
  logout,
  setIsLoading,
  setIsOpenLogin,
  setAuthToken,
  setSubmittedEmail,
  setResetLoginInfo,
  setIsOpenBasicModal,
  clearError,
  clearEngagementsError,
  clearPayoutError,
  clearProfileUpdateError,
  clearWalletTransactionsError,
  updateEngagementStatus,
  setCurrentPage,
} = authSlice.actions;

export default authSlice.reducer;
